
/* ============================================================
   LEFTOVRZ
   No dependencies, no build step. Everything degrades to a static page.

   The hero is static now: no scroll-scrubbed video, no frame cache, no
   knockout scrim. That removed the scrub loop, the 96-frame decoder, the
   pointer-driven letter light and the caption choreography along with it.
   ============================================================ */
(() => {
  'use strict';

  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp  = (n, a = 0, b = 1) => Math.min(b, Math.max(a, n));

  /* ---------- 2. kinetic hero, driven by scroll --------------
     The hero pins for its runway and scroll position drives two things:
     the decorative bands slide in opposite directions, and the statement
     assembles itself one word at a time.

     Smoothing is against elapsed TIME, not per frame. A fixed per-frame
     factor converges twice as fast at 120Hz as at 60Hz, so the feel would
     change with the display. TAU is the time constant in ms.            */
  const hero = document.querySelector('.hero');
  const rows = [...document.querySelectorAll('.kinetic__row')];
  const statement = document.querySelector('.hero__statement[data-fill]');
  let fillWords = [];

  if (statement) {
    const words = statement.textContent.trim().split(/\s+/);
    statement.textContent = '';
    words.forEach((word, i) => {
      const el = document.createElement('span');
      el.className = 'fill-word';
      el.textContent = word;
      statement.appendChild(el);
      if (i < words.length - 1) statement.appendChild(document.createTextNode(' '));
    });
    fillWords = [...statement.querySelectorAll('.fill-word')];
  }

  if (hero && !reduce.matches) {
    const TAU = 70;
    let eased = 0, lastT = 0, rafHero = 0;

    /* Measured once, not per frame. This used to call offsetHeight AND
       getBoundingClientRect on every tick — two forced synchronous layouts,
       sixty times a second, for the life of the page. scrollY needs no
       layout at all, so the geometry is cached and only re-read when the
       viewport actually changes size. */
    let heroTop = 0, runway = 0;
    const measure = () => {
      heroTop = hero.getBoundingClientRect().top + scrollY;
      runway  = hero.offsetHeight - innerHeight;
    };
    measure();
    addEventListener('resize', measure, { passive: true });

    const progress = () => runway <= 0 ? 0 : clamp((scrollY - heroTop) / runway);

    const paint = (p) => {
      for (const row of rows) {
        const dir = +row.dataset.dir || 1;
        const range = +row.dataset.range || 500;
        // start each band already offset so it is mid-travel on arrival
        row.style.transform = `translate3d(${(dir * (p - 0.5) * range).toFixed(1)}px,0,0)`;
      }
      // Each word owns a slice of the runway, so the sentence fills left to
      // right as you scroll rather than fading in as one block.
      const from = 0.10, to = 0.62;
      fillWords.forEach((el, i) => {
        const a = from + (to - from) * (i / Math.max(1, fillWords.length));
        const b = a + (to - from) / Math.max(1, fillWords.length) * 1.6;
        el.style.opacity = (0.48 + 0.52 * clamp((p - a) / (b - a))).toFixed(3);
      });
    };

    /* The loop now has an exit. It had none: it re-armed unconditionally, so
       it kept painting the hero at 60fps while the reader was metres down the
       page looking at something else. It stops once the easing has settled,
       and it stops outright when the hero is off screen. Scrolling wakes it. */
    let onScreen = true;

    const tick = (now) => {
      const dt = lastT ? Math.min(now - lastT, 64) : 16.7;
      lastT = now;
      const target = progress();
      eased += (target - eased) * (1 - Math.exp(-dt / TAU));
      paint(eased);
      if (!onScreen || Math.abs(target - eased) < 0.0004) { rafHero = 0; lastT = 0; return; }
      rafHero = requestAnimationFrame(tick);
    };
    const wakeHero = () => {
      if (!rafHero && onScreen) { lastT = 0; rafHero = requestAnimationFrame(tick); }
    };

    addEventListener('scroll', wakeHero, { passive: true });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        onScreen = e.isIntersecting;
        if (onScreen) wakeHero();
      }).observe(hero);
    }
    wakeHero();
  } else {
    fillWords.forEach((el) => { el.style.opacity = '1'; });
  }

  /* ---------- 3. cursor ------------------------------------
     Deliberately cheap. The two things that lived here before — a 22-blob
     metaball chain behind an SVG filter, and a WebGL fluid — both cost more
     than they earned: the filter re-rasterised on the CPU every frame, and
     the fluid forced everything blended above it to re-composite at 60fps.

     What is left is one element that only ever moves. A radial gradient
     painted once, positioned with transform, which the compositor handles
     without repainting anything — no filter, no blend mode, no canvas, no
     per-frame layout. The loop also parks itself when the pointer stops, so
     a still cursor costs literally nothing.

     Ink still flips against the ground it is over, so it stays visible on
     both the dark page and the amber hero. */
  let cursorFx = null;

  const startCursor = () => {
    if (cursorFx || reduce.matches) return;
    if (!matchMedia('(pointer: fine)').matches) return;
    const dot = document.getElementById('cursor-dot');
    if (!dot) return;

    const root = document.documentElement;
    const litMark = document.querySelector('.hero__mark--lit');

    // lead dot plus a short tail; sizes and opacities taper along it
    const N = 5;
    const pts = [];
    for (let i = 0; i < N; i++) {
      const el = document.createElement('i');
      const k = i / (N - 1);
      el.style.setProperty('--d', (30 - k * 17).toFixed(0) + 'px');
      el.style.setProperty('--o', (0.95 - k * 0.62).toFixed(2));
      dot.appendChild(el);
      pts.push({ el, x: -300, y: -300 });
    }

    const LIT_SEL = '.h2,.cta__h,.card__name,.drop__h,.lede,.price,' +
                    '.clock b,.stat__n,.sec__head .mono,.wall__note,.hero__kicker';
    let litNow = null, litRect = null, markRect = null;
    let hitX = NaN, hitY = NaN;
    let mx = -300, my = -300;
    let raf = 0, seen = false, lastHit = 0;
    // cached rects are only invalidated by things that actually move them
    const dropRects = () => { litRect = null; markRect = null; };
    addEventListener('scroll', dropRects, { passive: true });
    addEventListener('resize', dropRects, { passive: true });

    const frame = () => {
      /* One hit-test and at most one rect read per FRAME, not per event.
         The lit element's rect is cached and only re-read when the element
         under the pointer changes or the page scrolls — while you track
         along a single heading, which is the whole point of the effect,
         this costs nothing but arithmetic. */
      if (mx !== hitX || my !== hitY) {
        hitX = mx; hitY = my;
        const hit = document.elementFromPoint(mx, my);
        const lit = hit && hit.closest(LIT_SEL);
        if (lit !== litNow) {
          if (litNow) { litNow.style.removeProperty('--lx'); litNow.style.removeProperty('--ly'); }
          litNow = lit;
          litRect = lit ? lit.getBoundingClientRect() : null;
        } else if (lit && !litRect) {
          litRect = lit.getBoundingClientRect();
        }
        if (litNow && litRect) {
          litNow.style.setProperty('--lx', (mx - litRect.left).toFixed(0) + 'px');
          litNow.style.setProperty('--ly', (my - litRect.top).toFixed(0) + 'px');
        }
        if (litMark) {
          if (!markRect) markRect = litMark.getBoundingClientRect();
          if (markRect.bottom > 0 && markRect.top < innerHeight) {
            litMark.style.setProperty('--lx', (mx - markRect.left).toFixed(0) + 'px');
            litMark.style.setProperty('--ly', (my - markRect.top).toFixed(0) + 'px');
          }
        }
      }

      // head chases the pointer, each link chases the one in front
      pts[0].x += (mx - pts[0].x) * 0.30;
      pts[0].y += (my - pts[0].y) * 0.30;
      for (let i = 1; i < pts.length; i++) {
        pts[i].x += (pts[i - 1].x - pts[i].x) * 0.34;
        pts[i].y += (pts[i - 1].y - pts[i].y) * 0.34;
      }
      for (const p of pts) {
        p.el.style.transform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0)`;
      }

      // Park once the tail has caught up: a still pointer should not keep a
      // rAF alive for the life of the page.
      const last = pts[pts.length - 1];
      if (Math.abs(mx - last.x) < 0.4 && Math.abs(my - last.y) < 0.4) { raf = 0; return; }
      raf = requestAnimationFrame(frame);
    };
    const wake = () => { if (!raf) raf = requestAnimationFrame(frame); };

    addEventListener('pointermove', (e) => {
      mx = e.clientX; my = e.clientY;
      if (!seen) {
        seen = true;
        for (const p of pts) { p.x = mx; p.y = my; }
        dot.classList.add('is-on');
      }
      wake();

      const now = performance.now();
      if (now - lastHit > 120) {
        lastHit = now;
        const el = document.elementFromPoint(mx, my);
        const g = el && el.closest('[data-ground]');
        dot.dataset.ground = (g && g.dataset.ground === 'light') ? 'light' : 'dark';
      }

      /* Light only the element actually under the pointer, in its own
         coordinate space, so no transformed ancestor can stretch the
         gradient across the whole thing. One element, one rect read. */
      const qx = Math.round(mx / 2) * 2, qy = Math.round(my / 2) * 2;
      root.style.setProperty('--mx', qx + 'px');
      root.style.setProperty('--my', qy + 'px');

      // Everything that needs layout is deferred to the frame below. A
      // pointermove can fire well over a hundred times a second on a
      // high-refresh trackpad, and elementFromPoint plus
      // getBoundingClientRect each force a synchronous layout — running
      // them per event meant several forced layouts per frame for one
      // cursor position that only gets painted once.
    }, { passive: true });

    addEventListener('pointerleave', () => dot.classList.remove('is-on'));
    addEventListener('pointerenter', () => { if (seen) dot.classList.add('is-on'); });

    cursorFx = {
      start: wake,
      stop: () => { cancelAnimationFrame(raf); raf = 0; },
      show: (on) => dot.classList.toggle('is-on', on && seen),
    };
    wake();
  };

  /* ---------- 3b. motion mode, honored live in BOTH directions ----
     Flipping the OS setting while the page is open takes effect at once,
     and flipping it back re-arms rather than leaving things pinned. */
  const applyMotionMode = () => {
    if (reduce.matches) {
      if (cursorFx) { cursorFx.stop(); cursorFx.show(false); }
      document.querySelectorAll('.reveal, .animated-title')
        .forEach((el) => el.classList.add('is-in'));
    } else {
      if (cursorFx) { cursorFx.show(true); cursorFx.start(); } else startCursor();
    }
  };
  applyMotionMode();
  reduce.addEventListener('change', applyMotionMode);

  /* ---------- 4. carousels ---------------------------------- */
  for (const btn of document.querySelectorAll('.rail__btn')) {
    btn.addEventListener('click', () => {
      const rail = document.getElementById('rail-' + btn.dataset.rail);
      const card = rail.querySelector('.card');
      const step = card ? card.getBoundingClientRect().width + 2 : 260;
      rail.scrollBy({ left: step * +btn.dataset.dir, behavior: 'smooth' });
    });
  }
  // disable the arrows at each end rather than letting them no-op
  for (const rail of document.querySelectorAll('.rail')) {
    // scrollWidth/clientWidth force layout and only change on resize, so
    // they are measured there rather than on every scroll event. The button
    // list is resolved once too, instead of a querySelectorAll per scroll.
    const btns = [...document.querySelectorAll(
      `.rail__btn[data-rail="${rail.id.replace('rail-','')}"]`)];
    let max = 0;
    const measureRail = () => { max = rail.scrollWidth - rail.clientWidth - 2; };
    const sync = () => {
      for (const b of btns) {
        b.disabled = +b.dataset.dir < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= max;
      }
    };
    rail.addEventListener('scroll', sync, { passive: true });
    addEventListener('resize', () => { measureRail(); sync(); }, { passive: true });
    measureRail(); sync();
  }

  /* ---------- 5. reveal on scroll --------------------------- */
  if (!reduce.matches && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }
    }, { rootMargin: '0px 0px -12% 0px' });
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
  } else {
    document.querySelectorAll('.reveal').forEach(el => el.classList.add('is-in'));
  }

  /* ---------- 6. media slots --------------------------------
     Every [data-slot] fills itself from assets/media/<slot>.<ext>.
     Drop the picture in, reload, done -- no markup edit.

     Driven by a manifest rather than by probing each slot: probing cost
     one 404 per slot per extension, which buried the console and delayed
     load. The dev server builds the manifest from the directory; for a
     static deploy regenerate it with:
       node -e "const f=require('fs');f.writeFileSync('assets/media/manifest.json',JSON.stringify(f.readdirSync('assets/media').filter(n=>/\.(jpe?g|png|webp|avif)$/i.test(n))))"
  */
  /* Slot media is embedded, not fetched. This used to request a manifest
     from assets/media/ — a path that only exists on a dev server — so every
     load spent requests failing before rendering a single picture. Fill the
     map in #slot-media and the slots fill themselves; leave it empty and the
     cards stay the placeholders they are meant to be. */
  (() => {
    let media = {};
    try { media = JSON.parse(document.getElementById('slot-media').textContent); }
    catch { media = {}; }
    for (const el of document.querySelectorAll('[data-slot]')) {
      const src = media[el.dataset.slot];
      if (!src) continue;
      el.setAttribute('data-filled', '');
      if (el.classList.contains('cat')) {
        el.querySelector('a').style.backgroundImage = `url(${src})`;
      } else {
        const img = new Image();
        img.src = src; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
        el.prepend(img);
      }
    }
  })();


  /* ---------- 7. sticky nav state (Omnifood pattern) --------
     Over the film the nav floats on a gradient; past it, it needs a real
     ground or the links sit on whatever scrolls under them. */
  const nav = document.querySelector('.nav');
  if (nav && 'IntersectionObserver' in window) {
    new IntersectionObserver(([e]) => {
      nav.classList.toggle('is-stuck', !e.isIntersecting);
    }, { rootMargin: '-90px 0px 0px 0px' }).observe(hero);
  }

  /* ---------- 8. mobile nav ----------------------------------
     Removed. The nav ships its own toggle inline, because the nav renders
     on every template while this script is index-only. Two handlers on one
     button both fired, each flipping the menu, so a click opened and shut
     it in the same tick and the menu appeared to do nothing at all — on
     the home page only, which is exactly where both were loaded. */

  /* ---------- 9. brand filter -------------------------------- */
  const brandBtns = [...document.querySelectorAll('.brand')];
  if (brandBtns.length) {
    brandBtns.forEach((b) => b.addEventListener('click', () => {
      const want = b.dataset.brand;
      brandBtns.forEach((o) => o.classList.toggle('is-on', o === b));
      /* Vendor is "LEFTOVRZ" on every product here, so the strip matches a
         card's COLLECTION or its TAGS — whichever the button was built
         from — and falls back to the vendor for anything older. */
      for (const card of document.querySelectorAll('.card')) {
        const hay = new Set([
          ...(card.dataset.collection || '').split(' '),
          ...(card.dataset.tags || '').split(' '),
          ...(card.dataset.brand || '').split(' '),
        ].filter(Boolean));
        card.classList.toggle('is-hidden', want !== 'all' && !hay.has(want));
      }
      // the rail measures itself, so put it back to the start
      const rail = document.getElementById('rail-drops');
      if (rail) rail.scrollTo({ left: 0, behavior: 'smooth' });
    }));
  }

  /* ---------- 10. next-drop countdown ------------------------
     Next Friday 18:00 in the visitor's own timezone; rolls over on its own
     so it never needs a hardcoded date. */
  const cd = { d: 'cd-d', h: 'cd-h', m: 'cd-m', s: 'cd-s' };
  const cdEls = Object.fromEntries(
    Object.entries(cd).map(([k, id]) => [k, document.getElementById(id)]));
  if (cdEls.d) {
    const nextDrop = () => {
      const n = new Date(), t = new Date(n);
      t.setHours(18, 0, 0, 0);
      const ahead = (5 - t.getDay() + 7) % 7;        // 5 = Friday
      t.setDate(t.getDate() + ahead);
      if (t <= n) t.setDate(t.getDate() + 7);
      return t;
    };
    let due = nextDrop();
    const pad = (n) => String(n).padStart(2, '0');
    const tickClock = () => {
      let left = due - Date.now();
      if (left <= 0) { due = nextDrop(); left = due - Date.now(); }
      const sec = Math.floor(left / 1000);
      cdEls.d.textContent = pad(Math.floor(sec / 86400));
      cdEls.h.textContent = pad(Math.floor(sec / 3600) % 24);
      cdEls.m.textContent = pad(Math.floor(sec / 60) % 60);
      cdEls.s.textContent = pad(sec % 60);
    };
    tickClock();
    setInterval(tickClock, 1000);
  }

  /* ---------- 11. notify form -------------------------------- */
  const notify = document.getElementById('notify');
  if (notify) {
    const input = document.getElementById('notify-email');
    const msg = document.getElementById('notify-msg');
    const say = (text, state) => { msg.textContent = text; msg.dataset.state = state; };
    notify.addEventListener('submit', (e) => {
      e.preventDefault();
      const v = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) {
        input.setAttribute('aria-invalid', 'true');
        say('That address does not look right. Check it and try again.', 'err');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      // NOTE: front end only. Point this at the real list on deploy.
      say('You are on the list. Watch your inbox Friday.', 'ok');
      notify.querySelector('button').disabled = true;
      input.disabled = true;
    });
    input.addEventListener('input', () => {
      if (input.getAttribute('aria-invalid')) {
        input.removeAttribute('aria-invalid'); say('', '');
      }
    });
  }

  /* ---------- 12. crew vote ---------------------------------
     One tap, instant result. Counts are seeded locally so the block is
     never empty; swap the seed for a real endpoint when there is one. */
  const picks = [...document.querySelectorAll('.pick')];
  if (picks.length === 2) {
    const KEY = 'leftovrz.vote.v1';
    const read = () => {
      try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; }
    };
    const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch {} };

    const show = (state) => {
      const total = state.a + state.b;
      picks.forEach((p) => {
        const n = state[p.dataset.pick];
        const pct = total ? Math.round((n / total) * 100) : 0;
        p.querySelector('.pick__bar i').style.width = pct + '%';
        p.querySelector('.pick__pct').textContent = pct + '%';
        p.disabled = true;
        p.classList.toggle('is-win', n === Math.max(state.a, state.b));
      });
      const note = document.getElementById('vote-note');
      if (note) note.textContent = `${total.toLocaleString()} votes. Yours is counted.`;
    };

    const saved = read();
    if (saved) show(saved);

    picks.forEach((p) => p.addEventListener('click', () => {
      if (read()) return;
      const state = { a: 1284, b: 1157 };          // seed so the bars mean something
      state[p.dataset.pick] += 1;
      write(state);
      show(state);
    }));
  }

  /* ---------- 13. counters ----------------------------------- */
  const stats = [...document.querySelectorAll('.stat__n')];
  if (stats.length && !reduce.matches && 'IntersectionObserver' in window) {
    const io2 = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        io2.unobserve(e.target);
        const el = e.target, final = el.textContent;
        const num = parseFloat(final.replace(/[^0-9.]/g, ''));
        if (!isFinite(num)) continue;
        const pre = final.slice(0, final.search(/[0-9]/));
        const post = final.slice(final.search(/[0-9]/)).replace(/^[0-9.,]+/, '');
        const t0 = performance.now(), dur = 900;
        const step = (now) => {
          const k = clamp((now - t0) / dur);
          const eased = 1 - Math.pow(1 - k, 3);
          el.textContent = pre + Math.round(num * eased).toLocaleString() + post;
          if (k < 1) requestAnimationFrame(step); else el.textContent = final;
        };
        requestAnimationFrame(step);
      }
    }, { rootMargin: '0px 0px -20% 0px' });
    stats.forEach((el) => io2.observe(el));
  }

  /* ---------- 14. footer year -------------------------------- */
  const yr = document.getElementById('year');
  if (yr) yr.textContent = new Date().getFullYear();

  /* ---------- 15. animated title ----------------------------
     Ported from Zentry's AnimatedTitle. Each word starts pushed back and
     rotated in 3D at zero opacity, then they stagger into place. Zentry
     drives it with GSAP; here it is a CSS transition per word with an
     inline transition-delay for the stagger, so it costs no library and
     the browser composites it.

     The stagger is the whole trick: identical words arriving together
     read as a block fading in, arriving 40ms apart they read as type
     being set. */
  const titles = [...document.querySelectorAll('.animated-title[data-title]')];
  for (const el of titles) {
    const lines = el.dataset.title.split(/<br\s*\/?>/i);
    el.textContent = '';
    let n = 0;
    for (const line of lines) {
      const row = document.createElement('span');
      row.className = 'animated-line';
      for (const word of line.trim().split(/\s+/)) {
        const w = document.createElement('span');
        w.className = 'animated-word';
        w.textContent = word;
        w.style.transitionDelay = (n++ * 40) + 'ms';
        row.appendChild(w);
      }
      el.appendChild(row);
    }
  }
  if (titles.length) {
    if (reduce.matches || !('IntersectionObserver' in window)) {
      titles.forEach((el) => el.classList.add('is-in'));
    } else {
      const io3 = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('is-in');
          io3.unobserve(e.target);
        }
      }, { rootMargin: '0px 0px -15% 0px' });
      titles.forEach((el) => io3.observe(el));
    }
  }

  /* ---------- 16. per-block scroll entrances -----------------
     One observer, one class. Each block picks its own entrance in the
     markup via data-anim; this only decides WHICH children stagger and
     hands them an index. Items are chosen per block rather than "all
     children" so a section heading does not get treated as a grid item. */
  const ITEMS = '.card, .cat, .media, .pick, .brand, .stat, .clock span, .sec__head, .wall__intro, .split__type, .cta__h, .lede, .notify, .vote__vs, .drop__h, .hero__kicker';
  const blocks = [...document.querySelectorAll('[data-anim]')];

  for (const block of blocks) {
    let items = [...block.querySelectorAll(ITEMS)];
    // converge needs the pair plus the VS between them, in DOM order
    if (block.dataset.anim === 'converge') items = [...block.querySelectorAll('.vote__pair > *')];
    if (!items.length) items = [...block.children];
    items.forEach((el, i) => {
      el.classList.add('anim-item');
      el.style.setProperty('--i', Math.min(i, 12));   // cap the tail of long grids
    });
  }

  if (blocks.length) {
    if (reduce.matches || !('IntersectionObserver' in window)) {
      blocks.forEach((b) => b.classList.add('is-in'));
    } else {
      const io4 = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('is-in');
          io4.unobserve(e.target);
        }
      }, { rootMargin: '0px 0px -12% 0px' });
      blocks.forEach((b) => io4.observe(b));
    }
  }

  /* ---------- 17. category tiles lean toward the cursor -----
     Zentry's Features tilt. The handler writes two custom properties and
     nothing else, so it never touches layout; the transform lives in CSS. */
  if (!reduce.matches && matchMedia('(pointer: fine)').matches) {
    for (const tile of document.querySelectorAll('.cat')) {
      // Measured once on entry. Reading the rect on every move forced a
      // layout per event for a box that cannot move while you are inside it.
      let r = null;
      tile.addEventListener('pointerenter', () => { r = tile.getBoundingClientRect(); },
                            { passive: true });
      addEventListener('scroll', () => { if (r) r = null; }, { passive: true });
      tile.addEventListener('pointermove', (e) => {
        if (!r) r = tile.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        tile.classList.add('is-tilting');
        tile.style.setProperty('--ry', (px * 10).toFixed(2) + 'deg');
        tile.style.setProperty('--rx', (-py * 10).toFixed(2) + 'deg');
      }, { passive: true });
      tile.addEventListener('pointerleave', () => {
        tile.classList.remove('is-tilting');
        tile.style.setProperty('--ry', '0deg');
        tile.style.setProperty('--rx', '0deg');
      });
    }
  }

  /* ---------- 18. intro handover ----------------------------
     FLIP, not a crossfade. Measure where the intro mark is (First), measure
     where the hero mark sits (Last), Invert the difference into a transform
     and Play it. The mark you were looking at travels into the page and the
     hero mark takes over underneath, so it reads as one object rather than
     two logos dissolving.

     transform-origin is top-left in the CSS, which is what makes the
     translate + scale maths line up without a correction term. */
  const intro = document.getElementById('intro');
  if (intro) {
    const introMark = intro.querySelector('.intro__mark');
    const heroMark  = document.querySelector('.hero__mark');

    if (reduce.matches || !heroMark) {
      intro.remove();
    } else {
      /* Split the line per character and give each one its place in the
         wave. The rotation and the vertical offset come off a sine of the
         index, so the distortion arcs across the word instead of every
         letter jittering independently — that arc is what the reference
         is actually doing. --x pulls each glyph toward the centre at the
         peak, which crowds them the way the shot does. */
      /* Wrap the mark so the morph has a transform of its own, and give the
         halftone the wordmark's own file as its mask. */
      if (!reduce.matches && introMark && !introMark.parentElement.classList.contains('intro__morph')) {
        const wrap = document.createElement('span');
        wrap.className = 'intro__morph';
        wrap.style.position = 'relative';
        wrap.style.setProperty('--markurl', `url("${introMark.getAttribute('src')}")`);
        introMark.parentNode.insertBefore(wrap, introMark);
        wrap.appendChild(introMark);
      }

      const line = intro.querySelector('.intro__line');
      if (line && !reduce.matches) {
        const chars = [...line.textContent];
        const mid = (chars.length - 1) / 2;
        line.textContent = '';
        chars.forEach((ch, i) => {
          const span = document.createElement('span');
          span.textContent = ch;
          const w = Math.sin((i / Math.max(1, chars.length - 1)) * Math.PI * 1.4);
          span.style.setProperty('--i', i);
          span.style.setProperty('--r', (w * 13).toFixed(2) + 'deg');
          span.style.setProperty('--y', (w * -0.16).toFixed(3) + 'em');
          span.style.setProperty('--x', ((mid - i) * 0.09).toFixed(3) + 'em');
          line.appendChild(span);
        });
      }

      document.body.classList.add('intro-running');
      requestAnimationFrame(() => intro.classList.add('is-ready'));

      const hand = () => {
        const a = introMark.getBoundingClientRect();
        const b = heroMark.getBoundingClientRect();
        if (!a.width || !b.width) { finish(); return; }
        intro.classList.add('is-handing');
        introMark.style.transform =
          `translate3d(${(b.left - a.left).toFixed(1)}px, ${(b.top - a.top).toFixed(1)}px, 0)` +
          ` scale(${(b.width / a.width).toFixed(4)})`;
        intro.classList.add('is-done');       // the ground lifts mid-flight
        setTimeout(finish, 1200);
      };
      const finish = () => {
        document.body.classList.remove('intro-running');
        intro.remove();
      };

      /* The handover used to fire at 1750ms, while the letters were still
         morphing until ~2150ms — so the intro began flying out mid-word and
         .is-handing snapped the line to zero on top of it. That collision
         was the roughness. The last letter now lands at ~1940ms and the
         handover waits until 2300, leaving a beat of stillness on the
         finished word before it travels. Total ~3.5s. */
      setTimeout(hand, 2300);
      // never strand the page behind the overlay if anything above throws
      setTimeout(() => { if (document.getElementById('intro')) finish(); }, 6000);
    }
  }

  /* ---------- 19. product card colourways -------------------
     From codeaashu/The-day: swatches swap the product shot, stacked images
     crossfade on opacity, the active swatch gets a ring. Built here rather
     than written into the markup so eight cards do not need forty lines of
     HTML each, and so it stays in step with the media-slot naming.

     Shots load from assets/media/<slot>--<way>.jpg when they exist. Until
     then the swatch tints the placeholder, so the interaction is real and
     testable now and simply gets sharper once photography lands. */
  const WAYS = {
    bone:  '#D9D2C5',
    black: '#14110E',
    heat:  '#F8C038',
  };

  for (const card of document.querySelectorAll('#rail-drops .card, #rail-archive .card')) {
    const slot = card.dataset.slot;
    const media = card.querySelector('.card__media');
    const body  = card.querySelector('.card__body');
    if (!slot || !media || !body) continue;

    const names = Object.keys(WAYS);
    media.dataset.way = '';
    media.style.setProperty('--way', WAYS[names[0]]);

    // No colourway photography exists. Requesting it cost three failed
    // requests per card — forty-two on the page — for images that were then
    // removed on error anyway. The swatch tints the placeholder instead.
    const shots = {};

    const ways = document.createElement('div');
    ways.className = 'card__ways';
    ways.setAttribute('role', 'group');
    ways.setAttribute('aria-label', 'Colourway');

    names.forEach((name, i) => {
      const b = document.createElement('button');
      b.className = 'way';
      b.type = 'button';
      b.dataset.way = name;
      b.setAttribute('aria-pressed', String(i === 0));
      // colour alone must not carry the meaning, so each swatch is named
      b.setAttribute('aria-label', name[0].toUpperCase() + name.slice(1));
      b.innerHTML = `<i style="--sw:${WAYS[name]}"></i>`;
      ways.appendChild(b);
    });

    ways.addEventListener('click', (e) => {
      const b = e.target.closest('.way');
      if (!b) return;
      const name = b.dataset.way;
      for (const other of ways.querySelectorAll('.way'))
        other.setAttribute('aria-pressed', String(other === b));
      media.style.setProperty('--way', WAYS[name]);
      for (const [k, img] of Object.entries(shots)) img.classList.toggle('is-on', k === name);
    });

    const cta = document.createElement('button');
    cta.className = 'card__cta';
    cta.type = 'button';
    cta.innerHTML = '<span class="card__sweep"></span><span>Add to bag</span>';

    body.append(ways, cta);
    shots[names[0]]?.classList.add('is-on');
  }

  /* ---------- 20. break block: promo / in-stock carousel -----
     Three tracks (panel, giant word, shoe) each need their own copy of
     every slide. Hand-writing three parallel lists is the kind of thing
     that rots the first time someone adds a slide to two of them, so all
     three are built here from one manifest — which is also the Shopify
     seam. See the comment above #brk-slides.
     ---------------------------------------------------------- */
  (() => {
    const root = document.getElementById('break');
    if (!root) return;

    let slides = [];
    try { slides = JSON.parse(document.getElementById('brk-slides').textContent); }
    catch { slides = []; }
    slides = Array.isArray(slides) ? slides.filter((s) => s && typeof s === 'object') : [];
    // A section rendering as an empty stage is worse than no section: if
    // Shopify hands us zero blocks, take the whole band out.
    if (!slides.length) { root.remove(); return; }

    const panels = document.getElementById('brk-panels');
    const ghosts = document.getElementById('brk-ghosts');
    const art    = document.getElementById('brk-art');
    const roll   = document.getElementById('brk-roll');
    const bar    = document.getElementById('brk-bar');

    const esc = (t) => String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const isVideo = (s) =>
      String(s.media_type || '').toLowerCase() === 'video' ||
      /\.(mp4|webm|mov)(\?|#|$)/i.test(String(s.media || ''));

    /* ---- is this image actually background-free? -------------
       The block is built for cut-outs, but it must not TRUST that the
       next upload is one. An opaque studio shot dropped on a colour
       panel drags its own rectangle across it, which is the exact thing
       this block exists to avoid, so every image is probed and anything
       opaque is switched to the framed treatment instead.

       The probe samples a 32x32 downscale and asks whether the BORDER is
       transparent — a cut-out's edge pixels are empty whatever is in the
       middle, and that stays true for a shoe that fills the frame, where
       counting transparent pixels overall would not.

       Cross-origin media taints the canvas and getImageData throws;
       crossOrigin="anonymous" makes Shopify's CDN (which sends
       Access-Control-Allow-Origin) readable, and anything still
       unreadable falls back to the manifest's own flag. */
    const PROBE = 32;
    const probeCutout = (url) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = c.height = PROBE;
          const x = c.getContext('2d', { willReadFrequently: true });
          x.drawImage(img, 0, 0, PROBE, PROBE);
          const d = x.getImageData(0, 0, PROBE, PROBE).data;
          let edge = 0, clear = 0;
          for (let i = 0; i < PROBE; i++) {
            for (const [px, py] of [[i, 0], [i, PROBE - 1], [0, i], [PROBE - 1, i]]) {
              edge++;
              if (d[(py * PROBE + px) * 4 + 3] < 16) clear++;
            }
          }
          /* Same single read also yields the shoe's dominant colour, so
             deriving a panel costs no extra work. Averaged in a linear-ish
             space over solidly opaque pixels only — averaging through the
             feathered cut-out edge drags every shoe toward grey. */
          let r = 0, g = 0, b = 0, n = 0;
          for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 200) continue;
            r += d[i] * d[i]; g += d[i + 1] * d[i + 1]; b += d[i + 2] * d[i + 2]; n++;
          }
          const dom = n
            ? [Math.sqrt(r / n), Math.sqrt(g / n), Math.sqrt(b / n)]
            : null;
          resolve({ cutout: clear / edge > 0.7, dom });
        } catch { resolve(null); }        // tainted — caller decides
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

    /* ---- lifting a studio backdrop -----------------------------
       These files are RGBA PNGs whose alpha channel is entirely opaque —
       the format carries transparency, the pixels do not. Framing them on
       a white plate was the fallback; cutting them out is the answer.

       It floods inward FROM THE BORDER rather than keying every white
       pixel, because the midsole on these shoes is white too and a plain
       colour key punches holes through the middle of the shoe. Only white
       connected to the edge is backdrop. Near-threshold pixels get partial
       alpha so the cut does not read as scissors, and the result is
       trimmed to the subject so the shoe fills its own frame.
    */
    const keyOutBackdrop = (url) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const MAX = 1100;
          const k = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * k));
          const h = Math.max(1, Math.round(img.naturalHeight * k));
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          const x = c.getContext('2d', { willReadFrequently: true });
          x.drawImage(img, 0, 0, w, h);
          const im = x.getImageData(0, 0, w, h);
          const d = im.data;

          let sr = 0, sg = 0, sb = 0;
          for (const [px, py] of [[0,0],[w-1,0],[0,h-1],[w-1,h-1]]) {
            const i = (py * w + px) * 4;
            sr += d[i]; sg += d[i+1]; sb += d[i+2];
          }
          sr /= 4; sg /= 4; sb /= 4;
          // a dark corner is not a studio backdrop; leave that image alone
          if ((sr + sg + sb) / 3 < 200) { resolve(null); return; }

          const NEAR = 30, FAR = 74;
          const seen = new Uint8Array(w * h);
          const queue = new Int32Array(w * h);
          let head = 0, tail = 0, removed = 0;
          const push = (p) => { if (!seen[p]) { seen[p] = 1; queue[tail++] = p; } };
          for (let i = 0; i < w; i++) { push(i); push((h - 1) * w + i); }
          for (let j = 0; j < h; j++) { push(j * w); push(j * w + w - 1); }

          while (head < tail) {
            const p = queue[head++], i = p * 4;
            const dd = Math.hypot(d[i] - sr, d[i+1] - sg, d[i+2] - sb);
            if (dd > FAR) continue;
            if (dd <= NEAR) { d[i + 3] = 0; removed++; }
            else { d[i + 3] = Math.round(255 * ((dd - NEAR) / (FAR - NEAR))); continue; }
            const px = p % w, py = (p / w) | 0;
            if (px > 0)     push(p - 1);
            if (px < w - 1) push(p + 1);
            if (py > 0)     push(p - w);
            if (py < h - 1) push(p + w);
          }
          if (removed / (w * h) < 0.05) { resolve(null); return; }
          x.putImageData(im, 0, 0);

          /* Crop to the subject. The pair sits low with half the frame
             empty above it, and the CSS sizes the IMAGE, not the ink — so
             without this a shoe filling a third of its frame renders a
             third of the size the width setting asks for. */
          let minX = w, minY = h, maxX = -1, maxY = -1;
          for (let y = 0; y < h; y++) {
            for (let px = 0; px < w; px++) {
              if (d[(y * w + px) * 4 + 3] > 8) {
                if (px < minX) minX = px;
                if (px > maxX) maxX = px;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX < 0) { resolve(null); return; }
          const pad = Math.round(Math.max(w, h) * 0.015);
          minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
          maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
          const cw = maxX - minX + 1, ch = maxY - minY + 1;
          const out = document.createElement('canvas');
          out.width = cw; out.height = ch;
          out.getContext('2d').drawImage(c, minX, minY, cw, ch, 0, 0, cw, ch);
          resolve(out.toDataURL('image/png'));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

    /* ---- trimming a cut-out to its own edges -------------------
       Not background removal — that was tried and taken back out. This
       only applies to images that ALREADY have transparency, and only when
       there is a real margin of empty pixels around the subject.

       It exists because the CSS sizes the IMAGE, not the ink inside it. A
       cut-out exported from a catalogue shot keeps the original frame, so
       the shoe sits low with half the picture empty above it and renders a
       third of the size the width setting asks for. Below the threshold
       this does nothing at all, so a tightly cropped export pays no cost.
    */
    const TRIM_MIN = 0.08;                 // 8% of empty margin before bothering
    const trimToInk = (url) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const MAX = 1000;
          const k = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * k));
          const h = Math.max(1, Math.round(img.naturalHeight * k));
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          const x = c.getContext('2d', { willReadFrequently: true });
          x.clearRect(0, 0, w, h);
          x.drawImage(img, 0, 0, w, h);
          const d = x.getImageData(0, 0, w, h).data;

          let minX = w, minY = h, maxX = -1, maxY = -1;
          for (let y = 0; y < h; y++) {
            for (let px = 0; px < w; px++) {
              if (d[(y * w + px) * 4 + 3] > 8) {
                if (px < minX) minX = px;
                if (px > maxX) maxX = px;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX < 0) { resolve(null); return; }
          const slackX = 1 - (maxX - minX + 1) / w;
          const slackY = 1 - (maxY - minY + 1) / h;
          if (Math.max(slackX, slackY) < TRIM_MIN) { resolve(null); return; }

          const pad = Math.round(Math.max(w, h) * 0.015);
          minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
          maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
          const cw = maxX - minX + 1, ch = maxY - minY + 1;
          const out = document.createElement('canvas');
          out.width = cw; out.height = ch;
          out.getContext('2d').drawImage(c, minX, minY, cw, ch, 0, 0, cw, ch);
          resolve(out.toDataURL('image/png'));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

    /* ---- panel colour ----------------------------------------
       A panel is picked against its SHOE, not against the other slides —
       a black shoe needs the amber, a red one needs the deep teal. That
       rule is easy to state and easy to forget, so the block applies it
       itself: it takes the shoe's dominant colour and chooses whichever
       brand colour sits furthest from it.

       It chooses FROM A PALETTE rather than inventing a hue, because a
       free hue-rotate away from the shoe wanders straight out of the
       brand. A hand-set "tint" always wins — this only fills the gap. */
    /* The palette comes off the section when the theme supplies one, so the
       colours a slide can be given are editable in the customiser rather
       than frozen in this file. The literals stay as the fallback for the
       standalone preview, which has no Liquid behind it. */
    const PALETTE = (() => {
      const attr = (root.dataset.palette || '')
        .split(',').map((c) => c.trim())
        .filter((c) => /^#[0-9a-f]{3,8}$/i.test(c));
      return attr.length ? attr
        : ['#F8C038', '#B3372A', '#1E4B54', '#6B7A4A', '#EDE7DC', '#E4572E', '#3F4A5C'];
    })();

    const hexToRgb = (h) => {
      const v = h.replace('#', '');
      const n = v.length === 3
        ? v.split('').map((c) => parseInt(c + c, 16))
        : [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16));
      return n.some(Number.isNaN) ? null : n;
    };
    // Perceived luminance (Rec.709). Decides whether type on this panel
    // has to be dark or light, so nobody has to set "ink" by hand and get
    // it wrong.
    const luma = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    const inkFor = (hex) => {
      const c = hexToRgb(hex);
      return c && luma(c) > 0.55 ? 'dark' : 'light';
    };
    const hue = ([r, g, b]) => {
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
      if (!d) return 0;
      let h;
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      return (h * 60 + 360) % 360;
    };
    const hueGap = (a, b) => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; };

    /* Picking "furthest in RGB" sounds right and is not: maximum distance
       collapses onto whichever palette entry is brightest and most
       saturated, so every shoe — red, black, white — came back amber.

       What actually matters is that the shoe SEPARATES from the panel, and
       that the run of slides does not turn monochrome. So the score is
       mostly luminance contrast, which is what makes a shoe read at all,
       plus a smaller hue term for variety, and a penalty for a colour the
       previous slide already used. */
    const panelFor = (dom, used) => {
      if (!dom) return null;
      const dl = luma(dom), dh = hue(dom);
      let best = null, bestScore = -Infinity;
      for (const hex of PALETTE) {
        const c = hexToRgb(hex);
        if (!c) continue;
        const contrast = Math.abs(luma(c) - dl);        // 0..1, the main term
        const variety  = hueGap(hue(c), dh) / 180;      // 0..1
        let score = contrast * 1.0 + variety * 0.35;
        if (contrast < 0.22) score -= 0.6;              // too close to read
        if (used && used.has(hex)) score -= 0.45;       // don't repeat neighbours
        if (score > bestScore) { bestScore = score; best = hex; }
      }
      return best;
    };

    const SLOT_SVG =
      '<svg viewBox="0 0 48 32" aria-hidden="true">' +
      '<path d="M3 24h34c4 0 8-2 8-5 0-2-2-3-5-4l-9-3-7-6c-1-1-3-2-5-2H8" ' +
      'stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M3 24v3h42" stroke-linecap="round"/>' +
      '<path d="M14 12l3 4M20 14l3 4" stroke-linecap="round"/></svg>';

    /* One place that paints a slide's colour, so a derived tint arriving
       later goes through exactly the same path a hand-set one did. */
    const paintSlide = (i, hex, inkMode) => {
      const panel = panels.children[i], ghost = ghosts.children[i];
      const dark = inkMode === 'dark';
      if (panel) {
        panel.style.setProperty('--pp', hex);
        panel.style.setProperty('--ps', dark ? 'rgba(255,255,255,.34)' : 'rgba(0,0,0,.34)');
      }
      if (ghost) ghost.style.setProperty('--gc',
        dark ? 'rgba(20,17,13,.20)' : 'rgba(253,251,247,.26)');
      slides[i]._tint = hex;
      slides[i]._ink  = inkMode;
      if (i === n) {
        root.dataset.ink = inkMode;
        root.style.setProperty('--pt', hex);
      }
    };

    const pending = [], derived = [];

    slides.forEach((s, i) => {
      const tint  = s.tint || '#F8C038';
      const promo = String(s.kind || 'product').toLowerCase() === 'promo';
      const hasMedia = Boolean(s.media);

      const panel = document.createElement('div');
      panel.className = 'brk__panel' + (hasMedia && promo ? '' : ' brk__panel--bare');
      /* ink is derived from the tint unless it is stated. Setting it by
         hand was the step that got forgotten: a pale panel with light ink
         is unreadable, and nothing in the data said so. */
      const inkMode = s.ink ? String(s.ink).toLowerCase() : (s.tint ? inkFor(s.tint) : 'light');
      s._ink = inkMode;
      const dark = inkMode === 'dark';
      panel.style.setProperty('--pp', tint);
      // recorded so a slide deriving its own colour can see this one
      if (s.tint) { s._tint = tint; s._ink = inkMode; }
      // The scrim rides the PANEL as well as the root: during a slide both
      // panels are on screen at once, and a dark scrim over an incoming
      // light panel reads as a smear for the length of the transition.
      panel.style.setProperty('--ps', dark ? 'rgba(255,255,255,.34)' : 'rgba(0,0,0,.34)');
      panel.setAttribute('role', 'group');
      panel.setAttribute('aria-roledescription', 'slide');
      panel.setAttribute('aria-label', `${i + 1} of ${slides.length}`);
      panels.append(panel);

      const ghost = document.createElement('span');
      ghost.className = 'brk__ghost';
      ghost.textContent = s.ghost || (s.title || '').split('\n')[0] || '';
      ghost.style.setProperty('--gl', Math.max(ghost.textContent.length, 4));
      ghost.style.setProperty('--gc', dark ? 'rgba(20,17,13,.20)' : 'rgba(253,251,247,.26)');
      ghosts.append(ghost);

      /* Both the shoe and its empty-state slot live in the art track at
         the same index, exactly one of them shown, so paint() can address
         either by index without tracking which kind each slide got. */
      const shoe = document.createElement(isVideo(s) ? 'video' : 'img');
      shoe.className = 'brk__shoe';
      if (s.flip === true) shoe.style.setProperty('--fx', '-1');
      const slot = document.createElement('div');
      slot.className = 'brk__slot';
      slot.innerHTML = SLOT_SVG + '<span>Transparent PNG goes here</span>';

      if (hasMedia && promo) {
        // Lifestyle / promo media fills the panel edge to edge.
        const m = document.createElement(isVideo(s) ? 'video' : 'img');
        if (isVideo(s)) {
          Object.assign(m, { autoplay: true, muted: true, loop: true,
                             playsInline: true, preload: 'metadata' });
          m.setAttribute('muted', '');
        } else { m.loading = 'lazy'; m.decoding = 'async'; }
        m.src = s.media; m.alt = s.alt || '';
        if (s.focal) m.style.setProperty('--focal', s.focal);
        m.addEventListener('error', () => {
          m.remove(); panel.classList.add('brk__panel--bare');
        }, { once: true });
        panel.append(m);
        shoe.hidden = true; slot.hidden = true;
      } else if (hasMedia) {
        shoe.src = s.media; shoe.alt = s.alt || '';
        if (isVideo(s)) Object.assign(shoe, { autoplay: true, muted: true,
                                              loop: true, playsInline: true });
        slot.hidden = true;
        // A dead URL falls back to the marked slot, not a broken-image glyph.
        shoe.addEventListener('error', () => {
          shoe.hidden = true; slot.hidden = false; paint();
        }, { once: true });

        const declared = typeof s.cutout === 'boolean' ? s.cutout : null;
        if (declared === false) shoe.classList.add('brk__shoe--framed');

        /* One pixel read answers two independent questions: is this a
           cut-out, and what colour is the shoe. So it has to run when
           EITHER is still open. Gating it on the cut-out question alone
           meant any slide that declared "cutout": true — which is most of
           them — never got a colour derived at all. */
        const needsCut = declared === null, needsTint = !s.tint;
        if ((needsCut || needsTint) && !isVideo(s)) {
          /* Collected, not applied here. Every probe resolves at roughly the
             same moment, so a slide choosing inside its own callback cannot
             see what its neighbours chose — each looked at an empty set and
             independently picked the same colour. Chosen together, in order,
             after the loop. */
          pending.push(probeCutout(s.media).then((res) => {
            if (needsTint && res && res.dom) derived.push({ i, dom: res.dom, ink: s.ink });
            if (!needsCut) return;
            if (!res || res.cutout === false) {
              /* A studio shot: lift the backdrop and crop to the shoe.
                 Framing is only what happens when that cannot be done —
                 a dark or busy background, or a canvas the browser will
                 not let us read. */
              return keyOutBackdrop(s.media).then((keyed) => {
                if (keyed) { shoe.src = keyed; return; }
                shoe.classList.add('brk__shoe--framed');
              });
            }
            /* Already transparent: only trim, never re-key. */
            return trimToInk(s.media).then((tight) => { if (tight) shoe.src = tight; });
          }));
        }
      } else {
        shoe.hidden = true;
        // A promo slide legitimately has no shoe — only a PRODUCT slide
        // with nothing in it is an empty slot worth marking.
        if (promo) slot.hidden = true;
      }
      art.append(shoe, slot);

      const d = document.createElement('div');
      d.className = 'brk__desc';
      const badge = s.available === true  ? '<span class="brk__badge brk__badge--in">In stock</span>'
                  : s.available === false ? '<span class="brk__badge brk__badge--out">Sold out</span>'
                  : '';
      const specs = [s.size, s.condition].filter(Boolean)
        .map((v) => `<span class="brk__spec">${esc(v)}</span>`).join('');
      d.innerHTML = '<div class="brk__descin">' +
        `<p class="brk__kicker mono">${esc(s.kicker)}${badge}</p>` +
        `<h3 class="brk__title">${esc(s.title).replace(/\n/g, '<br>')}</h3>` +
        (specs ? `<p class="brk__specs">${specs}</p>` : '') +
        (s.price ? `<p class="brk__price">${esc(s.price)}</p>` : '') +
        (s.cta_label
          ? `<a class="brk__cta" href="${esc(s.cta_url || '#')}">${esc(s.cta_label)}</a>`
          : '') + '</div>';
      roll.append(d);
    });

    /* Assign derived colours once every probe is in, walking the slides in
       order so each can see what the one before it took. Only the IMMEDIATE
       neighbour is penalised, not every colour already used: the palette has
       two genuinely light entries and a rack of dark shoes all want a light
       panel, so forbidding repeats outright would push later slides onto
       panels their shoe cannot be seen against. Alternating amber and bone
       reads as deliberate; a black shoe on a near-black panel does not. */
    if (pending.length) {
      Promise.all(pending).then(() => {
        derived.sort((a, b) => a.i - b.i);
        for (const d of derived) {
          const prev = d.i > 0 ? slides[d.i - 1]._tint : null;
          const hex = panelFor(d.dom, prev ? new Set([prev]) : null);
          if (hex) paintSlide(d.i, hex, d.ink ? String(d.ink).toLowerCase() : inkFor(hex));
        }
      });
    }

    /* The roll is a fixed-height window, so its step must be the tallest
       slide or the longest copy gets clipped. Measured, not guessed: the
       copy comes from Shopify and can be any length. */
    const barEl = root.querySelector('.brk__bar');
    let rollh = 0, barh = 0;
    const sizeBar = () => {
      const h = Math.ceil(barEl.getBoundingClientRect().height)
              + parseFloat(getComputedStyle(barEl).bottom || 0);
      if (h === barh) return;
      barh = h;
      root.style.setProperty('--barh', `${h}px`);
    };
    const sizeRoll = () => {
      const tallest = [...roll.querySelectorAll('.brk__descin')]
        .reduce((m, el) => Math.max(m, el.getBoundingClientRect().height), 0);
      const h = Math.max(Math.ceil(tallest) + 22, 140);   // 10 padding + slack
      if (h === rollh) return;   // an unchanged write would feed the RO below
      rollh = h;
      root.style.setProperty('--rollh', `${h}px`);
    };

    const reduceMQ = matchMedia('(prefers-reduced-motion: reduce)');
    let n = 0;

    const paint = () => {
      root.style.setProperty('--n', n);
      root.dataset.ink = slides[n]._ink || 'light';
      if (slides[n]._tint) root.style.setProperty('--pt', slides[n]._tint);
      [...ghosts.children].forEach((el, i) => el.classList.toggle('is-on', i === n));
      [...panels.children].forEach((el, i) => el.classList.toggle('is-on', i === n));
      // art holds a shoe and a slot per slide, in pairs
      [...art.children].forEach((el, i) => {
        const idx = Math.floor(i / 2);
        el.classList.toggle('is-on', idx === n);
        el.classList.toggle('is-past', idx < n);
      });
      // Only the visible slide's link is tabbable; the rolled-away ones
      // would otherwise be focus traps outside the window.
      [...roll.children].forEach((d, i) => {
        const a = d.querySelector('a');
        if (a) a.tabIndex = i === n ? 0 : -1;
      });
    };

    const go = (d) => { n = (n + d + slides.length) % slides.length; paint(); arm(); };

    for (const b of root.querySelectorAll('[data-brk]')) {
      b.addEventListener('click', () => go(+b.dataset.brk));
    }
    // (no arrows to hide when there is only one slide — swipe simply no-ops)

    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); go(-1); }
    });

    /* swipe — pointer events, so one path covers touch and trackpad drag */
    let x0 = null, y0 = null;
    const stage = root.querySelector('.brk__stage');
    stage.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; },
                           { passive: true });
    stage.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      x0 = y0 = null;
      // A swipe has to be mostly horizontal, or a diagonal flick while
      // scrolling the page would change slide under the reader.
      if (Math.abs(dx) > 38 && Math.abs(dx) > Math.abs(dy) * 1.4) go(dx < 0 ? 1 : -1);
    }, { passive: true });
    stage.addEventListener('pointercancel', () => { x0 = y0 = null; }, { passive: true });

    /* Two-finger trackpad swipe. This is NOT a pointer gesture — the trackpad
       reports it as a wheel event carrying deltaX, so the drag handler above
       never sees it and the carousel felt dead to anyone on a laptop.

       Deltas are accumulated rather than acted on individually: one physical
       flick arrives as a long stream of small deltas, so reacting per event
       would skip several slides at once. Once the run crosses the threshold
       the gesture is latched, and only unlatches after the deltas stop, which
       is what makes one flick move exactly one slide.

       Vertical intent is left alone so the page still scrolls; only a clearly
       horizontal gesture is consumed, and that one is preventDefault'd to stop
       the browser turning it into a back-navigation swipe. */
    /* One flick, one slide. This is a slide, not a scrollbar.

       Two things fought that. A trackpad flick keeps sending momentum events
       long after your fingers lift, and that stream contains small
       opposite-sign jitter — so a naive "direction changed = new gesture"
       test unlatched mid-tail and let the same flick advance again and again.

       So: tiny deltas are ignored as jitter rather than intent; while latched,
       further movement the SAME way is absorbed and resets nothing; and going
       back requires a SUSTAINED push the other way, accumulated past the same
       threshold a fresh gesture needs. The latch only truly clears once the
       events stop for 220ms, which is the real end of the gesture. */
    const WHEEL_STEP = 90;    // how much horizontal travel makes one slide
    const WHEEL_MIN  = 2;     // below this it is momentum noise
    let accX = 0, latched = 0, wheelIdle = 0;   // latched: 0 | -1 | 1

    stage.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;   // vertical: not ours
      e.preventDefault();
      if (Math.abs(e.deltaX) < WHEEL_MIN) return;

      clearTimeout(wheelIdle);
      wheelIdle = setTimeout(() => { accX = 0; latched = 0; }, 220);

      const dir = e.deltaX < 0 ? -1 : 1;

      if (latched) {
        if (dir === latched) { accX = 0; return; }   // same way: absorb the tail
        accX += e.deltaX;                            // other way: must be earned
        if (Math.abs(accX) > WHEEL_STEP) { latched = dir; go(dir); accX = 0; }
        return;
      }

      accX += e.deltaX;
      if (Math.abs(accX) > WHEEL_STEP) {
        latched = accX < 0 ? -1 : 1;
        go(latched);
        accX = 0;
      }
    }, { passive: false });

    /* Autoplay. The bar is the clock made visible, driven by the same
       timestamp that decides when to advance rather than by a parallel CSS
       animation that could drift out of step with it. */
    /* The bar is a CSS animation, and the advance is a timer. It used to be a
       requestAnimationFrame loop writing a custom property every frame —
       sixty style writes a second, forever, purely to move a 3px hairline.
       The compositor can animate that on its own; JS only needs to know when
       the slide is over. */
    const HOLD = 6200;
    let timer = 0, paused = false, onScreen = false;
    const stop = () => {
      clearTimeout(timer); timer = 0;
      root.classList.remove('is-playing');
    };
    const arm = () => {
      clearTimeout(timer); timer = 0;
      root.classList.remove('is-playing');
      if (reduceMQ.matches || paused || !onScreen || slides.length < 2) return;
      // restart the bar's animation from zero
      void bar.offsetWidth;
      root.classList.add('is-playing');
      timer = setTimeout(() => { n = (n + 1) % slides.length; paint(); arm(); }, HOLD);
    };

    for (const [ev, v] of [['pointerenter', true], ['pointerleave', false],
                           ['focusin', true], ['focusout', false]]) {
      root.addEventListener(ev, () => { paused = v; arm(); });
    }
    document.addEventListener('visibilitychange', () => { paused = document.hidden; arm(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((es) => {
        for (const e of es) { onScreen = e.isIntersecting; arm(); }
      }, { threshold: 0.35 }).observe(root);
    } else { onScreen = true; }

    reduceMQ.addEventListener('change', arm);

    /* Re-measure whenever the copy reflows, not only when the window
       resizes: at a narrow width a title wraps to two lines and grows past
       a measurement taken at a wider one, and the overflow then bleeds the
       next slide into the window. A ResizeObserver catches every cause —
       viewport, font swap, or Shopify copy that changed length. */
    let rafId = 0;
    const scheduleSize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => { sizeRoll(); sizeBar(); });
    };
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(scheduleSize);
      for (const el of roll.querySelectorAll('.brk__descin')) ro.observe(el);
      ro.observe(barEl);
    }
    addEventListener('resize', scheduleSize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { sizeRoll(); sizeBar(); });
    }

    sizeRoll();
    sizeBar();
    paint();
    arm();
  })();

})();
